import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, Fade, Stack, Tooltip, Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import { makeStyles } from "@mui/styles";
import { VirtualItem } from '@tanstack/react-virtual';
import classNames from "classnames";
import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import useBulkDashboards from "../../../hooks/useBulkDashboards";
import useBulkWorkspaces from "../../../hooks/useBulkWorkspaces";
import useVirtualTable from '../../../hooks/useVirtualTable';
import useWorkspacesTable from "../../../hooks/useWorkspacesTable";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";
import InvestorsIconFC from "../../atoms/InvestorsIconFC";
import NoWorkspacesFoundView from "../../molecules/workspaces-empty/NoWorkspacesFoundView";
import WorkspacesTableHeader from "../../molecules/workspaces-table/WorkspacesTableHeader";
import WorkspacesTableRow from "../../molecules/workspaces-table/WorkspacesTableRow";
import { collectionKey as dashboardSelectionKey } from "../../organisms/dashboards/DashboardsView";
import { collectionKey } from "../../pages/InvestorsPage";
import { FallbackLoading } from "../../templates/loader";
import CreateEditInvestorModal from "../investors/CreateEditInvestorModal";

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: 1300,
            maxHeight: 700,
            width: 'min-content',
            height: 'fit-content',
            padding: 20,
            borderRadius: 16,
        },
    },
    successDialog: {
        '& .MuiDialog-paper': {
            minWidth: 'unset',
            width: 'fit-content',
        },
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.5rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        height: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    selected :{
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: 'black',
    },
    table: {
        width: '100%',
        height: 'calc(100vh - 120px)',
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
    createButton: {
        width: 'auto',
        height: 40,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    cancelButton: {
        width: 100,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        width: 100,
        borderRadius: 20,
        background: theme.palette.primary.main,
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '20px !important',
            height: '20px !important',
        }
    },
    checkBoxText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
    title: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        color: '#000',
    },
    icon: {
        color: theme.colors.primary['500'],
        width: 50,
        height: 50,
    },
}));

export const ShareWorkspaceTableHeader = ['Name', 'Companies', 'Investors', 'Associated tags', '[company match]'];

const AddCompaniesToSharedWorkspaceModal: React.FC<{
    isOpen: boolean,
    loading?: boolean,
    onAdd?: () => void,
    onClose: () => void,
}> = ({ isOpen, loading, onAdd, onClose }) => {
    const classes = useStyles();
    const { matchedAssociatedDashboards, setMatchedAssociatedDashboards } = useContext(WorkspacesContext);
    const { loading: localLoading, rows, filters, handleFilterRows } = useWorkspacesTable(undefined, undefined, true, false);
    const { allRows, rowVirtualizer, hasNextPage, virtualRowsRef, virtualHeaderRef }
        = useVirtualTable({ key: 'add-companies-to-shared-workspace-modal-175', source: rows, limit: 20, itemHeight: 60 });
    const { getBulkWorkspaces, addToBulk, clearBulk, associateToDashboards } = useBulkWorkspaces();
    const { getBulkDashboards } = useBulkDashboards();

    const [createWorkspaceModalOpen, setCreateWorkspaceModalOpen] = useState<boolean>(false);
    const [matchLoading, setMatchLoading] = useState<boolean>(false);
    const [allChecked, setAllChecked] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);

    const bulkCount = useMemo(() => getBulkWorkspaces(collectionKey).length, [getBulkWorkspaces]);

    const matchedAssociatedDashboardsCount = useMemo(() =>
        Object.values(matchedAssociatedDashboards).some(dashboards => dashboards.length > 0)
    , [matchedAssociatedDashboards]);

    const handleMatch = useCallback(async() => {
        await new Promise<void>((resolve) => {
            setMatchLoading(true);
            if (!bulkCount)
                addToBulk(collectionKey, rows);

            const timeOut = setTimeout(() => {
                clearTimeout(timeOut);
                associateToDashboards(collectionKey, getBulkDashboards(dashboardSelectionKey).map(({dashboard}) => dashboard));
                setMatchLoading(false);
                resolve();
            }, 2000);
        });
    }, [addToBulk, associateToDashboards, bulkCount, getBulkDashboards, rows]);

    const handleAdd = useCallback(() => {
        onAdd?.();
        setSuccess(true);
    }, [onAdd]);

    useEffect(() => {
        clearBulk(collectionKey);
        setMatchedAssociatedDashboards({});

        return () => {
            clearBulk(collectionKey);
            setMatchedAssociatedDashboards({});
        };
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (allChecked)
            addToBulk(collectionKey, rows);
        else
            clearBulk(collectionKey);
    // eslint-disable-next-line
    }, [allChecked]);

    useEffect(() => {
        if (success) {
            const timeOut = setTimeout(() => {
                clearTimeout(timeOut);
                onClose();
            }, 4000);

            return () => {
                clearTimeout(timeOut);
                setSuccess(false);
            }
        }
    // eslint-disable-next-line
    }, [success]);

    return (<>
        <Dialog className={classNames(classes.dialog, success && classes.successDialog)} open={isOpen} onClose={onClose}>
            {success ? (
                <DialogContent className={classes.dialogContent}>
                    <Stack direction="column" spacing={2} alignItems="center" width="100%" p="20px 80px">
                        <Fade in={success} timeout={1000}>
                            <CheckCircleIcon className={classes.icon} sx={{ transform: 'translateX(-5px)' }} />
                        </Fade>
                        <Box />
                        <Typography className={classes.title} textAlign="center">
                            {'Companies successfully added to'}<br />
                            {'shared workspaces!'}
                        </Typography>
                    </Stack>
                </DialogContent>
            ) : (<>
                <DialogTitle className={classes.dialogTitle}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                            <InvestorsIconFC color="rgb(163, 167, 172)" />
                            <span>{'Add to shared workspaces'}</span>
                        </Stack>
                        {!bulkCount ? (
                            <Tooltip placement="top-start" title={
                                <Typography fontSize="0.9rem">
                                    {`Select shared workspaces`}<br/>
                                    {`before running a match`}
                                </Typography>}
                                componentsProps={{tooltip:{sx:{
                                    display: 'flex', alignItems: 'center', borderRadius: 4,
                                    backgroundColor: 'rgba(97, 97, 97)', width: '230px', height: '60px',  padding: 4,
                                }}}}>
                                <Button variant="outlined"
                                    className={classes.createButton}
                                    sx={{
                                        background: 'rgba(0, 0, 0, 0.05) !important',
                                        color: 'rgba(0, 0, 0, 0.5) !important',
                                        cursor: 'auto !important',
                                    }}
                                >{`Run match`} </Button>
                            </Tooltip>
                        ) : (
                            <LoadingButton variant="outlined"
                                className={classes.createButton}
                                loading={matchLoading}
                                onClick={handleMatch}
                            >{`Run match`} </LoadingButton>
                        )}
                    </Stack>
                </DialogTitle>
                <Divider sx={{ margin: '8px 0'}} />
                <DialogContent className={classes.dialogContent}>
                    <Stack className={classes.table}>
                        {localLoading ? (<FallbackLoading />) : (<>
                            <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                                <Box width="100%" height="fit-content" overflow="hidden">
                                    <Box className={classes.header} ref={virtualHeaderRef}>
                                        <WorkspacesTableHeader
                                            columns={ShareWorkspaceTableHeader}
                                            filters={filters}
                                            onFilter={handleFilterRows} />
                                    </Box>
                                </Box>
                                {!!rows?.length ? (<>
                                    <Box width="100%" height="100%" overflow="hidden">
                                        <Box className={classes.rows} ref={virtualRowsRef}
                                            sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                            {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                                <Box className={classes.row}
                                                    sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                                    key={'add-companies-to-shared-workspace-modal-314-' + virtualRow.index} >
                                                    {(virtualRow.index > allRows.length - 1) && hasNextPage
                                                        ? (<FallbackLoading />)
                                                        : (<WorkspacesTableRow
                                                                workspace={allRows[virtualRow.index]}
                                                                columns={ShareWorkspaceTableHeader}
                                                                collectionKey={collectionKey} />)}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </>) : (
                                    <Stack alignItems="flex-start" justifyContent="flex-start" width="100%" height="100%">
                                        <Divider sx={{ width: '100%' }} />
                                        <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                            <NoWorkspacesFoundView noResult />
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        </>)}
                    </Stack>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%"
                        sx={{ transform: 'translateX(-10px)'}}>
                        <Checkbox edge="end"
                            className={classes.checkBox}
                            checked={allChecked}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setAllChecked(prev => !prev);
                            }}
                            disableRipple />
                        <Typography className={classes.checkBoxText}>
                            {'Select all'}&nbsp;
                            <Typography component="span" className={classes.checkBoxText}
                                color={`${theme.colors.neutral['600']} !important`} fontWeight="500 !important" display="inline">
                                {`(${rows.length} shared workspace${rows.length > 1 ? 's': ''})`}
                            </Typography>
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" width="100%">
                        <Typography className={classes.selected}>{`${bulkCount} shared workspace${bulkCount > 1 ? 's' : ''} selected`}</Typography>
                        <Button className={classes.cancelButton}
                            onClick={onClose}> {'Cancel'} </Button>
                        <LoadingButton variant="contained"
                            className={classes.saveButton}
                            loading={loading}
                            disabled={!matchedAssociatedDashboardsCount}
                            onClick={handleAdd}
                        > {'Add'} </LoadingButton>
                    </Stack>
                </DialogActions>
            </>)}
        </Dialog>
        {createWorkspaceModalOpen && (
            <CreateEditInvestorModal
                isOpen={createWorkspaceModalOpen}
                onClose={() => {
                    setCreateWorkspaceModalOpen(false);
                }}
            />
        )}
    </>);
}

export default memo(AddCompaniesToSharedWorkspaceModal);